import React, { useContext, useMemo } from 'react';
import { HStack, Spinner, Text, useToast } from '@chakra-ui/react';
import { useOktaAuth } from '@okta/okta-react';
import { useSplunkLogger } from '@resolutionlife/ui';
import { captureException } from '@sentry/react';
import download from 'downloadjs';

import { fetchWithAuth } from '../auth';
import env from '../configs/env';
import { useDownloadBlob } from '../hooks/use-download-blob/use-download-blob';

type PiiReportContextProps = {
  download: () => void;
};

type UseDownloadPiiReportBag = {
  downloadReport: () => void;
};

const LOADING_TOAST_ID = 'pii-audit-report-loading';

export const useDownloadPiiReport = (): UseDownloadPiiReportBag => {
  const toast = useToast();
  const oktaAuthResult = useOktaAuth();
  const oktaAuth = oktaAuthResult?.oktaAuth;
  const { downloadBlob } = useDownloadBlob();
  const splunkLogger = useSplunkLogger();

  const downloadReport = () => {
    (async () => {
      // Display a toast while processing the request
      toast({
        id: LOADING_TOAST_ID,
        title: (
          <HStack spacing={4} data-testid={LOADING_TOAST_ID}>
            <Text data-testid="title">Generating PII Audit Report</Text>
            <Spinner color="white" size="xs" />
          </HStack>
        ),
        description: 'This may take some time to complete.',
        duration: null,
        isClosable: true,
        position: 'top',
        status: 'info',
      });

      try {
        if (!oktaAuth) {
          throw new Error('Okta auth not available');
        }
        // oktaAuth.getAccessToken() is synchronous (returns string | undefined),
        // so it must not be awaited (SonarQube typescript:S4123).
        const token = oktaAuth.getAccessToken();
        if (!token) {
          throw new Error('Failed to get access token');
        }
        const { blob, filename } = await downloadBlob({
          url: `${env.api.host}${env.api.paths.downloadPiiReport}`,
          token,
          file: {
            name: 'pii-audit-report',
            type: 'csv',
          },
          fetchWithAuth,
        });
        download(blob, filename);
      } catch (e) {
        toast({
          title: 'Unable to download PII Audit Report',
          status: 'error',
          position: 'top',
          duration: 10000,
        });
        captureException(e);
        splunkLogger.error('PII audit report download failed', { eventCategory: 'pii_review_error', errorMessage: String(e) });
      } finally {
        // Close the toast when the download has finished/failed
        toast.close(LOADING_TOAST_ID);
      }
    })();
  };

  return { downloadReport };
};

const PiiReportContext = React.createContext<PiiReportContextProps>({
  download: () => {
    return;
  },
});

export const PiiReportProvider: React.FC<{ value?: PiiReportContextProps }> = (
  props
) => {
  const { downloadReport } = useDownloadPiiReport();

  const contextValue = useMemo(
    () => ({
      download: () => { downloadReport(); },
    }),
    [downloadReport]
  );

  return <PiiReportContext.Provider value={contextValue} {...props} />;
};

export const usePiiReport = (): PiiReportContextProps => {
  return useContext(PiiReportContext);
};
